import React, { useState, useEffect } from "react";

import { Box } from "@mui/material";
import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import useActionProduct from "../../../../../hooks/useActionProducts";
import { Product } from "../../../../../models/product";
import { Searches } from "../../../../../models/shipeezi.table";
import Modal from "../../../../Generic/Modal";
import useStyles from "./styles";

interface ModalAddProductProps {
  showAddProductModal: boolean;
  setShowAddProductModal: (showAddProductModal: boolean) => void;
  addProducts: (products: Product[]) => void;
  addedProducts?: number[];
}

const ModalAddProduct = ({
  showAddProductModal,
  setShowAddProductModal,
  addProducts,
  addedProducts,
}: ModalAddProductProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const { getAllProducts, countProducts } = useActionProduct();

  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [selectedProductsIds, setSelectedProductsIds] = useState<number[]>([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searches, setSearches] = useState<Searches>([]);

  const { data: products, isFetching: loadingProducts } = getAllProducts(
    page,
    pageSize,
    searches,
    undefined,
    undefined,
    { minimalList: true, endpointVersion: "v2" }
  );
  const { data: total, isFetching: loadingTotalItems } = countProducts(
    page,
    pageSize,
    searches,
    undefined,
    undefined,
    { minimalList: true, endpointVersion: "v2" }
  );
  const totalItems = total ?? (products?.length || 0);

  useEffect(() => {
    const selectedProductsAux: Product[] = [];

    for (const productId of selectedProductsIds) {
      if (addedProducts && !addedProducts.includes(productId)) {
        const product = products?.find((product) => String(product.id) === String(productId));

        if (product) selectedProductsAux.push(product);
      }
    }

    setSelectedProducts(selectedProductsAux);
  }, [selectedProductsIds]);

  const displaySelectedProducts = () => {
    if (addedProducts?.length) {
      return selectedProductsIds.concat(addedProducts);
    }

    return selectedProductsIds;
  };

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handleSearch = (searches: Searches) => {
    setPage(0);
    setSearches(searches);
  };

  const columns: Column[] = [
    { name: "sku", title: `${translate("sku")} #` },
    { name: "description", title: translate("productName") },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      right: ["handleSearch"],
    },
    handleSearch: {
      inputPlaceholder: translate("typePressEnter"),
      inputSearchMode: "enter",
      onSearch: handleSearch,
    },
  };

  return (
    <Modal
      data-cy="add-product-modal"
      title={translate("addProduct")}
      open={showAddProductModal}
      handleClose={() => setShowAddProductModal(false)}
      showActionButtons
      actionButtonProps={{
        saveDisabled: selectedProductsIds.length === 0,
        onSave: () => addProducts(selectedProducts),
        dataCySave: "save-product-button",
      }}
    >
      <Box className={classes.dialogContent} data-cy="productsListGrid">
        <ShipeeziNewGrid
          data={products || []}
          totalItems={totalItems}
          columns={columns}
          loadingData={loadingProducts}
          emptyData={translate("noProductsToShow")}
          selecteds={displaySelectedProducts()}
          handleSelect={setSelectedProductsIds}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={totalItems > pageSize ? [10, 30, 50, 100] : []}
          onChangePageSize={setPageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
          showSelection={totalItems !== 0}
          showFooterGrid={totalItems !== 0 || loadingProducts || loadingTotalItems}
        />
      </Box>
    </Modal>
  );
};

export default ModalAddProduct;
